import React from 'react';
import { Stars, Sparkles, User, Settings, HelpCircle, FileText, Shield, Heart, Palette, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const More = () => {
  const { user } = useAuth();

  // Define all items (mobile will show Divine Guidance, desktop won't)
  const allItems = [
    {
      icon: Stars,
      title: 'Divine Guidance',
      description: 'Astrology insights and personalized readings',
      path: '/app/divine-guidance',
      color: 'from-purple-400 to-purple-600',
      mobileOnly: true  // Only show on mobile
    },
    {
      icon: Palette,
      title: 'Vision Boards',
      description: 'Create visual manifestation boards for your dreams',
      path: '/app/vision-board',
      color: 'from-pink-400 to-pink-600'
    },
    {
      icon: Sparkles,
      title: 'Love Vault',
      description: 'Your saved affirmations and milestones',
      path: '/app/wall',
      color: 'from-rose-400 to-rose-600'
    }
  ];

  // Filter items based on screen size
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show all items on mobile, filter out mobileOnly items on desktop
  const moreItems = isMobile ? allItems : allItems.filter(item => !item.mobileOnly);

  const supportItems = [
    {
      icon: HelpCircle,
      title: 'Help Center',
      description: 'Browse FAQs and feature guides',
      path: '/help-center'
    },
    {
      icon: MessageCircle,
      title: 'Live Support',
      description: 'Chat with our AI support agent',
      path: '/live-support'
    },
    {
      icon: Shield,
      title: 'Crisis Support',
      description: 'Immediate help and resources',
      path: '/sos-resources'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 pb-24">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">More Features</h1>
          <p className="text-gray-600">Additional tools for your transformation journey</p>
        </div>

        {/* Main Features */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">✨ Explore & Discover</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {moreItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="p-6 bg-white/80 backdrop-blur-sm border-rose-200 hover:shadow-lg transition-all">
                  <Link to={item.path} className="block">
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Support & Settings */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🛠️ Settings & Support</h2>
          <div className="grid grid-cols-1 gap-3">
            {supportItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="bg-white/80 backdrop-blur-sm border-rose-200">
                  <Link to={item.path} className="block p-4 hover:bg-rose-25 transition-colors">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
                        <Icon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Love Score Summary */}
        <Card className="p-6 bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
          <div className="text-center">
            <div className="text-4xl mb-4">💖</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome, {user?.name?.split(' ')[0] || user?.name}!</h3>
            <p className="text-gray-600 mb-4">
              You're part of the Constant Love™ movement. Keep growing your Love Score through 
              daily affirmations, journaling, and Love Garden sessions.
            </p>
            <div className="bg-white/50 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Tip:</strong> Visit your Love Garden daily to build consistency and watch your inner garden bloom! 🌱
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default More;